import { MarketBusiness, MarketCompetitor, MarketData, MarketKeyword, MarketPageAudit } from '../../../common/hooks/use-market-api.hook';
import { classifyAudit, vitalLevel } from './health-checks';
import {
    GapRow,
    authorityLevel,
    authorityScore,
    biggestMoves,
    biggestRival,
    buyingShare,
    fallingDemand,
    followSplit,
    gapOwners,
    historyChange,
    landingPages,
    linkDiversity,
    listingCompleteness,
    mergeGaps,
    nearTop,
    pageTwoCount,
    pctChange,
    quickWins,
    ratioOf,
    rivalSize,
    segmentCounts,
    sharedPageOneShare,
    spamLevel,
    worstBranch,
    youOf,
    You,
} from './market-analysis';
import { cleanDomain, rankedCompetitors } from './market-presentation';
import { InsightSpec, Msg, ROUTES, SpecTone, money, msg, pct, pctOf, prioritise, searchSegment } from './insight-spec';

/**
 * The written reading at the top of each Business page: what changed, what is
 * working, what to fix — computed from the market read, each with a link to
 * the page that explains it. Pure; the words are translation keys.
 */

const n = (v: number | null | undefined): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);
const has = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const keywordsOf = (data: Pick<MarketData, 'keywords'>): MarketKeyword[] => data.keywords?.keywords ?? [];

/* ─────────────────────────────── shared findings ─────────────────────────────── */

function movementInsight(data: MarketData): InsightSpec | null {
    const mv = data.overview?.movement;
    if (!mv) return null;
    const up = n(mv.up);
    const down = n(mv.down);
    if (!up && !down && !n(mv.new) && !n(mv.lost)) return null;
    const tone: SpecTone = up > down * 1.1 ? 'good' : down > up * 1.1 ? 'bad' : 'neutral';
    return {
        id: 'movement',
        tone,
        title: msg('INSIGHT.MOVEMENT', { up, down }),
        detail: msg('INSIGHT.MOVEMENT_DETAIL', { new: n(mv.new), lost: n(mv.lost) }),
        action: { label: msg('ACTION.SEE_CHANGES'), href: searchSegment(tone === 'bad' ? 'down' : 'up') },
    };
}

function visitsChangeInsight(data: MarketData): InsightSpec | null {
    const ch = historyChange(data.history?.overview, 'estimatedMonthlyVisits');
    if (!ch) return null;
    const p = pctChange(ch.current, ch.previous);
    if (p == null || Math.abs(p) < 5) return null;
    return {
        id: 'visits',
        tone: p > 0 ? 'good' : 'bad',
        title: msg(p > 0 ? 'INSIGHT.VISITS_UP' : 'INSIGHT.VISITS_DOWN', { pct: pctOf(Math.abs(p)), month: { month: ch.since } }),
        detail: msg('INSIGHT.VISITS_DETAIL', { from: ch.previous, to: ch.current }),
        action: { label: msg('ACTION.SEE_SEARCH'), href: ROUTES.search },
    };
}

function pageTwoInsight(data: MarketData): InsightSpec | null {
    const overall = pageTwoCount(data.overview);
    const sample = segmentCounts(keywordsOf(data)).quick;
    const count = overall ?? sample;
    if (!count) return null;
    const first = quickWins(keywordsOf(data), 1)[0];
    return {
        id: 'page-two',
        tone: 'watch',
        title: msg('INSIGHT.PAGE_TWO', { n: count }, count),
        detail: first
            ? msg('INSIGHT.PAGE_TWO_DETAIL', { keyword: first.keyword ?? '', volume: n(first.searchVolume), position: n(first.position) })
            : msg('INSIGHT.PAGE_TWO_HINT'),
        action: { label: msg('ACTION.SEE_QUICK_WINS'), href: searchSegment('quick') },
    };
}

function rivalInsight(data: MarketData, you: You): InsightSpec | null {
    const rivals = rankedCompetitors(data);
    if (!rivals.length) return null;
    const big = biggestRival(data);
    const r = big ? ratioOf(big.organicKeywords, you.keywords) : null;
    const closest = rivals[0];
    const detail = closest?.sharedKeywords
        ? msg('INSIGHT.RIVAL_DETAIL', { rival: cleanDomain(closest.domain), shared: n(closest.sharedKeywords) })
        : undefined;
    if (big && r != null && r >= 1.25) {
        return {
            id: 'rival',
            tone: 'watch',
            title: msg('INSIGHT.RIVAL_BIGGER', { rival: cleanDomain(big.domain), ratio: r }),
            detail,
            action: { label: msg('ACTION.SEE_COMPETITORS'), href: ROUTES.rivals },
        };
    }
    if (r != null && r <= 0.8) {
        return {
            id: 'rival',
            tone: 'good',
            title: msg('INSIGHT.RIVAL_AHEAD'),
            detail,
            action: { label: msg('ACTION.SEE_COMPETITORS'), href: ROUTES.rivals },
        };
    }
    if (big && r != null) {
        return {
            id: 'rival',
            tone: 'neutral',
            title: msg('INSIGHT.RIVAL_LEVEL', { rival: cleanDomain(big.domain) }),
            detail,
            action: { label: msg('ACTION.SEE_COMPETITORS'), href: ROUTES.rivals },
        };
    }
    return null;
}

function gapsInsight(rows: GapRow[]): InsightSpec | null {
    if (!rows.length) return null;
    const total = rows.reduce((s, r) => s + r.searchVolume, 0);
    const best = rows.find((r) => !r.brand) ?? rows[0];
    return {
        id: 'gaps',
        tone: 'watch',
        title: msg('INSIGHT.GAPS', { n: rows.length }, rows.length),
        detail: msg('INSIGHT.GAPS_DETAIL', { total, keyword: best.keyword }),
        action: { label: msg('ACTION.SEE_GAPS'), href: ROUTES.gaps },
    };
}

function healthInsight(audit: MarketPageAudit | null | undefined): InsightSpec | null {
    if (!audit || !has(audit.score)) return null;
    const { counts } = classifyAudit(audit);
    const score = Math.round(audit.score);
    const tone: SpecTone = score < 60 || counts.error > 0 ? 'bad' : score < 80 ? 'watch' : 'good';
    return {
        id: 'health',
        tone,
        title: msg(tone === 'good' ? 'INSIGHT.HEALTH_GOOD' : 'INSIGHT.HEALTH_BAD', { score }),
        detail: counts.error + counts.warning > 0 ? msg('INSIGHT.HEALTH_DETAIL', { errors: counts.error, warnings: counts.warning }) : undefined,
        action: { label: msg('ACTION.SEE_HEALTH'), href: ROUTES.health },
    };
}

function profileInsight(b: MarketBusiness | null | undefined): InsightSpec | null {
    if (!b) return null;
    const action = { label: msg('ACTION.SEE_PROFILE'), href: ROUTES.profile };
    if (b.notFound) return { id: 'profile', tone: 'bad', title: msg('INSIGHT.PROFILE_MISSING'), detail: msg('INSIGHT.PROFILE_MISSING_DETAIL'), action };
    if (b.isClaimed === false) return { id: 'profile', tone: 'bad', title: msg('INSIGHT.PROFILE_UNCLAIMED'), detail: msg('INSIGHT.PROFILE_UNCLAIMED_DETAIL'), action };
    if (has(b.rating) && b.rating < 4) {
        return { id: 'profile', tone: 'bad', title: msg('INSIGHT.PROFILE_RATING_LOW', { rating: b.rating }), action };
    }
    if (has(b.rating) && b.rating >= 4.5 && n(b.totalReviews ?? b.reviews) >= 10) {
        const reviews = n(b.totalReviews ?? b.reviews);
        return { id: 'profile', tone: 'good', title: msg('INSIGHT.PROFILE_RATING_GOOD', { rating: b.rating, reviews }, reviews), action };
    }
    return null;
}

function brokenLinksInsight(data: MarketData): InsightSpec | null {
    const broken = n(data.backlinks?.brokenBacklinks);
    if (!broken) return null;
    return {
        id: 'broken-links',
        tone: 'watch',
        title: msg('INSIGHT.BROKEN_LINKS', { n: broken }, broken),
        detail: msg('INSIGHT.BROKEN_LINKS_DETAIL'),
        action: { label: msg('ACTION.SEE_BACKLINKS'), href: ROUTES.backlinks },
    };
}

/* ─────────────────────────────── overview ─────────────────────────────── */

/**
 * The market in a sentence or three: reach, what it is worth, what is within
 * reach, and who is ahead. Each part only when its number is there.
 */
export function marketSummary(data: MarketData): Msg[] {
    const o = data.overview;
    if (!o) return [];
    const you = youOf(data);
    const parts: Msg[] = [];
    if (!you.keywords && !you.visits) {
        return [msg('OVERVIEW.SUMMARY.INVISIBLE', { domain: you.domain })];
    }
    parts.push(msg('OVERVIEW.SUMMARY.REACH', { domain: you.domain, keywords: you.keywords, visits: you.visits }, you.keywords));
    if (n(o.trafficValue) > 0) parts.push(msg('OVERVIEW.SUMMARY.VALUE', { money: money(n(o.trafficValue)) }));
    const p2 = pageTwoCount(o);
    if (p2) parts.push(msg('OVERVIEW.SUMMARY.PAGE_TWO', { n: p2 }, p2));
    const big = biggestRival(data);
    const r = big ? ratioOf(big.organicKeywords, you.keywords) : null;
    if (big && r != null && r >= 1.25) parts.push(msg('OVERVIEW.SUMMARY.BEHIND', { rival: cleanDomain(big.domain), ratio: r }));
    else if (r != null && r <= 0.8) parts.push(msg('OVERVIEW.SUMMARY.AHEAD'));
    return parts;
}

export function overviewInsights(data: MarketData, limit = 6): InsightSpec[] {
    const you = youOf(data);
    const specs = [
        visitsChangeInsight(data),
        movementInsight(data),
        pageTwoInsight(data),
        rivalInsight(data, you),
        gapsInsight(mergeGaps(data.keywordGaps)),
        healthInsight(data.pageAudit),
        profileInsight(data.business),
        brokenLinksInsight(data),
    ].filter(Boolean) as InsightSpec[];
    return prioritise(specs, limit, ['visits', 'movement']);
}

/* ─────────────────────────────── search ─────────────────────────────── */

export function searchInsights(data: MarketData, limit = 6): InsightSpec[] {
    const kws = keywordsOf(data);
    const specs: (InsightSpec | null)[] = [visitsChangeInsight(data), movementInsight(data)];

    const { gain, loss } = biggestMoves(kws);
    if (gain) {
        specs.push({
            id: 'gain',
            tone: 'good',
            title: msg('SEARCH.INSIGHT.GAIN', { keyword: gain.keyword ?? '', from: n(gain.previousPosition), to: n(gain.position) }),
            detail: msg('SEARCH.INSIGHT.VOLUME', { volume: n(gain.searchVolume) }),
        });
    }
    if (loss) {
        specs.push({
            id: 'loss',
            tone: 'bad',
            title: msg('SEARCH.INSIGHT.LOSS', { keyword: loss.keyword ?? '', from: n(loss.previousPosition), to: n(loss.position) }),
            detail: msg('SEARCH.INSIGHT.LOSS_DETAIL', { volume: n(loss.searchVolume) }),
            action: { label: msg('ACTION.SEE_DECLINED'), href: searchSegment('down') },
        });
    }

    specs.push(pageTwoInsight(data));

    const near = nearTop(kws, 1)[0];
    const nearCount = kws.filter((k) => has(k.position) && k.position >= 4 && k.position <= 10).length;
    if (near) {
        specs.push({
            id: 'near-top',
            tone: 'watch',
            title: msg('SEARCH.INSIGHT.NEAR_TOP', { n: nearCount }, nearCount),
            detail: msg('SEARCH.INSIGHT.NEAR_TOP_DETAIL', { keyword: near.keyword ?? '', position: n(near.position), volume: n(near.searchVolume) }),
            action: { label: msg('ACTION.SEE_PAGE_ONE'), href: searchSegment('page1') },
        });
    }

    const pages = landingPages(kws);
    const totalVisits = kws.reduce((s, k) => s + n(k.estimatedVisits), 0);
    if (pages[0] && totalVisits > 0) {
        const s = pages[0].visits / totalVisits;
        specs.push({
            id: 'top-page',
            tone: s >= 0.5 && pages.length > 1 ? 'watch' : 'neutral',
            title: msg(s >= 0.5 && pages.length > 1 ? 'SEARCH.INSIGHT.ONE_PAGE' : 'SEARCH.INSIGHT.TOP_PAGE', { page: pages[0].url, pct: pct(s) }),
            detail: s >= 0.5 && pages.length > 1 ? msg('SEARCH.INSIGHT.ONE_PAGE_DETAIL') : undefined,
        });
    }

    const buying = buyingShare(kws);
    if (buying != null) {
        if (buying >= 0.5) specs.push({ id: 'intent', tone: 'good', title: msg('SEARCH.INSIGHT.BUYERS', { pct: pct(buying) }) });
        else if (buying < 0.25)
            specs.push({ id: 'intent', tone: 'watch', title: msg('SEARCH.INSIGHT.RESEARCHERS', { pct: pct(buying) }), detail: msg('SEARCH.INSIGHT.RESEARCHERS_DETAIL') });
    }

    const falling = fallingDemand(kws)[0];
    if (falling) {
        specs.push({
            id: 'falling',
            tone: 'watch',
            title: msg('SEARCH.INSIGHT.FALLING', { keyword: falling.keyword ?? '', pct: pctOf(Math.abs(n(falling.yearlyChange))) }),
            detail: msg('SEARCH.INSIGHT.FALLING_DETAIL'),
        });
    }

    const snippets = kws.filter((k) => k.featuredSnippet).length;
    if (snippets) specs.push({ id: 'snippets', tone: 'good', title: msg('SEARCH.INSIGHT.SNIPPETS', { n: snippets }, snippets) });

    return prioritise(specs.filter(Boolean) as InsightSpec[], limit, ['visits', 'movement']);
}

/* ─────────────────────────────── competitors ─────────────────────────────── */

/** One line per rival: their size against yours, their visits, their hold on your shared searches. */
export function rivalReading(c: MarketCompetitor, you: You): Msg[] {
    const parts: Msg[] = [];
    const size = rivalSize(c, you);
    if (size) parts.push(msg(`COMPETITORS.READ.${size.size.toUpperCase()}`, { ratio: size.ratio }));
    const vr = ratioOf(c.estimatedMonthlyVisits, you.visits);
    if (vr != null && vr >= 1.5) parts.push(msg('COMPETITORS.READ.MORE_VISITS', { ratio: vr }));
    else if (vr != null && vr <= 0.67) parts.push(msg('COMPETITORS.READ.FEWER_VISITS', { ratio: 1 / vr }));
    const s = sharedPageOneShare(c);
    if (s != null) parts.push(msg('COMPETITORS.READ.PAGE_ONE', { pct: pct(s) }));
    return parts;
}

export function competitorInsights(data: MarketData, limit = 6): InsightSpec[] {
    const rivals = rankedCompetitors(data);
    if (!rivals.length) return [];
    const you = youOf(data);
    const specs: InsightSpec[] = [];

    const closest = rivals[0];
    specs.push({
        id: 'closest',
        tone: 'neutral',
        title: msg('COMPETITORS.INSIGHT.CLOSEST', { rival: cleanDomain(closest.domain), shared: n(closest.sharedKeywords) }),
        detail: rivalReading(closest, you)[0],
    });

    const big = biggestRival(data);
    const r = big ? ratioOf(big.organicKeywords, you.keywords) : null;
    if (big && r != null && r >= 1.25) {
        specs.push({ id: 'biggest', tone: 'watch', title: msg('INSIGHT.RIVAL_BIGGER', { rival: cleanDomain(big.domain), ratio: r }) });
    }

    const smaller = rivals.filter((c) => rivalSize(c, you)?.size === 'smaller').length;
    if (smaller > 0) {
        specs.push({ id: 'ahead', tone: 'good', title: msg('COMPETITORS.INSIGHT.AHEAD', { n: smaller, total: rivals.length }, smaller) });
    }

    const strongest = rivals
        .map((c) => ({ c, s: sharedPageOneShare(c) }))
        .filter((x) => x.s != null && n(x.c.sharedKeywords) >= 5)
        .sort((a, b) => (b.s as number) - (a.s as number))[0];
    if (strongest && (strongest.s as number) >= 0.4) {
        specs.push({
            id: 'page-one',
            tone: 'watch',
            title: msg('COMPETITORS.INSIGHT.PAGE_ONE', { rival: cleanDomain(strongest.c.domain), pct: pct(strongest.s as number) }),
            detail: msg('COMPETITORS.INSIGHT.PAGE_ONE_DETAIL'),
            action: { label: msg('ACTION.SEE_GAPS'), href: ROUTES.gaps },
        });
    }

    const richest = [...rivals].filter((c) => has(c.trafficValue)).sort((a, b) => n(b.trafficValue) - n(a.trafficValue))[0];
    const vr = richest ? ratioOf(richest.trafficValue, you.trafficValue) : null;
    if (richest && vr != null && vr >= 2) {
        specs.push({
            id: 'value',
            tone: 'watch',
            title: msg('COMPETITORS.INSIGHT.VALUE', { rival: cleanDomain(richest.domain), money: money(n(richest.trafficValue)), ratio: vr }),
        });
    }

    if (!(data.keywordGaps ?? []).length) {
        specs.push({
            id: 'no-gaps',
            tone: 'neutral',
            title: msg('COMPETITORS.INSIGHT.NO_GAPS'),
            action: { label: msg('ACTION.SEE_GAPS'), href: ROUTES.gaps },
        });
    }

    return prioritise(specs, limit, ['closest']);
}

/* ─────────────────────────────── gaps ─────────────────────────────── */

export function gapInsights(rows: GapRow[], limit = 6): InsightSpec[] {
    if (!rows.length) return [];
    const specs: InsightSpec[] = [];
    const total = rows.reduce((s, r) => s + r.searchVolume, 0);
    specs.push({
        id: 'total',
        tone: 'watch',
        title: msg('GAPS.INSIGHT.TOTAL', { n: rows.length }, rows.length),
        detail: msg('GAPS.INSIGHT.TOTAL_DETAIL', { volume: total }),
    });

    const best = rows.find((r) => !r.brand);
    if (best) {
        specs.push({
            id: 'start',
            tone: 'good',
            title: msg('GAPS.INSIGHT.START', { keyword: best.keyword }),
            detail:
                best.difficulty != null
                    ? msg('GAPS.INSIGHT.START_DETAIL', { volume: best.searchVolume, difficulty: best.difficulty })
                    : msg('SEARCH.INSIGHT.VOLUME', { volume: best.searchVolume }),
        });
    }

    const buying = rows.filter((r) => r.intent === 'transactional' || r.intent === 'commercial').length;
    if (buying) specs.push({ id: 'buying', tone: 'neutral', title: msg('GAPS.INSIGHT.BUYING', { n: buying }, buying), detail: msg('GAPS.INSIGHT.BUYING_DETAIL') });

    const shared = rows.filter((r) => r.rivals.length > 1).length;
    if (shared) specs.push({ id: 'shared', tone: 'neutral', title: msg('GAPS.INSIGHT.SHARED', { n: shared }, shared), detail: msg('GAPS.INSIGHT.SHARED_DETAIL') });

    const owners = gapOwners(rows);
    if (owners.length > 1) {
        specs.push({ id: 'owner', tone: 'neutral', title: msg('GAPS.INSIGHT.OWNER', { rival: owners[0].rival, volume: owners[0].volume }) });
    }

    const brand = rows.filter((r) => r.brand).length;
    if (brand) specs.push({ id: 'brand', tone: 'neutral', title: msg('GAPS.INSIGHT.BRAND', { n: brand }, brand) });

    return prioritise(specs, limit, ['total', 'start']);
}

/* ─────────────────────────────── backlinks ─────────────────────────────── */

export function backlinkInsights(data: MarketData, limit = 6): InsightSpec[] {
    const b = data.backlinks;
    if (!b) return [];
    const specs: InsightSpec[] = [];

    const ch = historyChange(data.history?.backlinks, 'referringDomains');
    if (ch && ch.current !== ch.previous) {
        const d = ch.current - ch.previous;
        specs.push({
            id: 'domains-change',
            tone: d > 0 ? 'good' : 'bad',
            title: msg(d > 0 ? 'BACKLINKS.INSIGHT.DOMAINS_UP' : 'BACKLINKS.INSIGHT.DOMAINS_DOWN', { n: Math.abs(d), month: { month: ch.since } }, Math.abs(d)),
        });
    }

    const score = authorityScore(b.rank);
    const level = authorityLevel(score);
    if (level) {
        const tone: SpecTone = level === 'low' ? 'bad' : level === 'modest' ? 'watch' : 'good';
        specs.push({
            id: 'authority',
            tone,
            title: msg(`BACKLINKS.INSIGHT.AUTHORITY_${level.toUpperCase()}`, { score: score as number }),
            detail: msg('BACKLINKS.INSIGHT.AUTHORITY_DETAIL'),
        });
    }

    const domains = n(b.referringDomains);
    if (has(b.referringDomains) && domains < 25) {
        specs.push({ id: 'few', tone: 'watch', title: msg('BACKLINKS.INSIGHT.FEW', { n: domains }, domains), detail: msg('BACKLINKS.INSIGHT.FEW_DETAIL') });
    }

    const broken = n(b.brokenBacklinks);
    if (broken > 0) {
        specs.push({
            id: 'broken',
            tone: n(b.backlinks) > 0 && broken / n(b.backlinks) >= 0.1 ? 'bad' : 'watch',
            title: msg('BACKLINKS.INSIGHT.BROKEN', { n: broken }, broken),
            detail: msg('BACKLINKS.INSIGHT.BROKEN_DETAIL'),
        });
    }

    const spam = spamLevel(b.spamScore);
    if (spam) {
        specs.push({
            id: 'spam',
            tone: spam === 'high' ? 'bad' : spam === 'moderate' ? 'watch' : 'good',
            title: msg(`BACKLINKS.INSIGHT.SPAM_${spam.toUpperCase()}`, { score: n(b.spamScore) }),
            detail: spam === 'low' ? undefined : msg('BACKLINKS.INSIGHT.SPAM_DETAIL'),
        });
    }

    const follow = followSplit(b);
    if (follow && domains > 0 && follow.nofollow / domains > 0.5) {
        specs.push({ id: 'nofollow', tone: 'watch', title: msg('BACKLINKS.INSIGHT.NOFOLLOW', { pct: pct(follow.nofollow / domains) }), detail: msg('BACKLINKS.INSIGHT.NOFOLLOW_DETAIL') });
    }

    const diversity = linkDiversity(b);
    if (diversity != null && diversity < 0.5 && domains >= 10) {
        specs.push({
            id: 'diversity',
            tone: 'watch',
            title: msg('BACKLINKS.INSIGHT.DIVERSITY', { sites: domains, networks: n(b.referringSubnets) }),
            detail: msg('BACKLINKS.INSIGHT.DIVERSITY_DETAIL'),
        });
    }

    return prioritise(specs, limit, ['domains-change']);
}

/* ─────────────────────────────── health ─────────────────────────────── */

export function healthInsights(audit: MarketPageAudit | null | undefined, limit = 6): InsightSpec[] {
    if (!audit) return [];
    const specs: InsightSpec[] = [];
    const { issues, passed, counts } = classifyAudit(audit);

    if (has(audit.score)) {
        const score = Math.round(audit.score);
        const level = score >= 80 ? 'GOOD' : score >= 60 ? 'OK' : 'BAD';
        specs.push({ id: 'score', tone: level === 'GOOD' ? 'good' : level === 'OK' ? 'watch' : 'bad', title: msg(`HEALTH.INSIGHT.SCORE_${level}`, { score }) });
    }

    const firstError = issues.find((i) => i.severity === 'error');
    if (counts.error) {
        specs.push({
            id: 'errors',
            tone: 'bad',
            title: msg('HEALTH.INSIGHT.ERRORS', { n: counts.error }, counts.error),
            detail: firstError ? msg('HEALTH.INSIGHT.FIX_FIRST', { issue: { tkey: `BUSINESS.HEALTH.CHECK.${firstError.check.toUpperCase()}.TITLE`, fallback: firstError.check } }) : undefined,
        });
    }
    if (counts.warning) specs.push({ id: 'warnings', tone: 'watch', title: msg('HEALTH.INSIGHT.WARNINGS', { n: counts.warning }, counts.warning) });

    const lcp = audit.timing?.largestPaint;
    const lcpLevel = vitalLevel('largestPaint', lcp);
    if (lcpLevel === 'bad' || lcpLevel === 'watch') {
        specs.push({ id: 'slow', tone: lcpLevel === 'bad' ? 'bad' : 'watch', title: msg('HEALTH.INSIGHT.SLOW', { seconds: n(lcp) / 1000 }), detail: msg('HEALTH.INSIGHT.SLOW_DETAIL') });
    } else if (lcpLevel === 'good') {
        specs.push({ id: 'fast', tone: 'good', title: msg('HEALTH.INSIGHT.FAST', { seconds: n(lcp) / 1000 }) });
    }

    const cls = audit.timing?.layoutShift;
    if (vitalLevel('layoutShift', cls) === 'bad') {
        specs.push({ id: 'shift', tone: 'watch', title: msg('HEALTH.INSIGHT.SHIFT'), detail: msg('HEALTH.INSIGHT.SHIFT_DETAIL') });
    }

    if (n(audit.errors) > 0) {
        specs.push({ id: 'resources', tone: 'bad', title: msg('HEALTH.INSIGHT.RESOURCES', { n: n(audit.errors) }, n(audit.errors)), detail: msg('HEALTH.INSIGHT.RESOURCES_DETAIL') });
    }

    if (passed.includes('is_https')) specs.push({ id: 'https', tone: 'good', title: msg('HEALTH.INSIGHT.HTTPS') });
    if (!issues.length) specs.push({ id: 'clean', tone: 'good', title: msg('HEALTH.INSIGHT.CLEAN') });

    return prioritise(specs, limit, ['score']);
}

/* ─────────────────────────────── profile ─────────────────────────────── */

export function profileInsights(b: MarketBusiness | null | undefined, limit = 6): InsightSpec[] {
    if (!b || b.notFound) return [];
    const specs: InsightSpec[] = [];
    const locations = b.locations ?? [];

    if (b.isClaimed === false) {
        specs.push({ id: 'unclaimed', tone: 'bad', title: msg('INSIGHT.PROFILE_UNCLAIMED'), detail: msg('INSIGHT.PROFILE_UNCLAIMED_DETAIL') });
    } else if (n(b.unclaimed) > 0 && locations.length > 1) {
        specs.push({ id: 'unclaimed', tone: 'bad', title: msg('PROFILE.INSIGHT.UNCLAIMED_BRANCHES', { n: n(b.unclaimed), total: locations.length }, n(b.unclaimed)) });
    }

    if (has(b.rating)) {
        if (b.rating >= 4.5) specs.push({ id: 'rating', tone: 'good', title: msg('PROFILE.INSIGHT.RATING_GOOD', { rating: b.rating }) });
        else if (b.rating >= 4) specs.push({ id: 'rating', tone: 'neutral', title: msg('PROFILE.INSIGHT.RATING_OK', { rating: b.rating }), detail: msg('PROFILE.INSIGHT.RATING_OK_DETAIL') });
        else specs.push({ id: 'rating', tone: 'bad', title: msg('INSIGHT.PROFILE_RATING_LOW', { rating: b.rating }), detail: msg('PROFILE.INSIGHT.RATING_LOW_DETAIL') });
    }

    const reviews = n(b.totalReviews ?? b.reviews);
    if (reviews < 20) specs.push({ id: 'reviews', tone: 'watch', title: msg('PROFILE.INSIGHT.FEW_REVIEWS', { n: reviews }, reviews), detail: msg('PROFILE.INSIGHT.FEW_REVIEWS_DETAIL') });
    else if (reviews >= 100) specs.push({ id: 'reviews', tone: 'good', title: msg('PROFILE.INSIGHT.MANY_REVIEWS', { n: reviews }, reviews) });

    const worst = worstBranch(b);
    if (worst && has(worst.rating) && worst.rating < 4) {
        specs.push({ id: 'worst', tone: 'bad', title: msg('PROFILE.INSIGHT.WORST_BRANCH', { branch: worst.city || worst.title || '—', rating: worst.rating }) });
    }

    if (n(b.photos) < 10) specs.push({ id: 'photos', tone: 'watch', title: msg('PROFILE.INSIGHT.FEW_PHOTOS', { n: n(b.photos) }, n(b.photos)), detail: msg('PROFILE.INSIGHT.FEW_PHOTOS_DETAIL') });

    const complete = listingCompleteness(b);
    const missing = (complete?.missing ?? []).filter((f) => f !== 'photos');
    if (missing.length) {
        specs.push({
            id: 'missing',
            tone: 'watch',
            title: msg('PROFILE.INSIGHT.MISSING_FIELDS', { n: missing.length }, missing.length),
            detail: msg('PROFILE.INSIGHT.MISSING_FIELDS_DETAIL', {
                fields: { list: missing.map((f) => `BUSINESS.PROFILE.FIELD.${f.toUpperCase()}`) },
            }),
        });
    }

    return prioritise(specs, limit);
}
